import { useCallback, useRef } from 'react'
import { ShapeUtil, TLBaseShape, TLShapePartial, Rectangle2d, HTMLContainer, useEditor, useValue } from 'tldraw'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import DocumentIcon from './DocumentIcon'
import { shapeNodeToSvgImage } from '../utils/shapeNodeToSvgImage'

export type DocPreviewShape = TLBaseShape<
  'DocPreview',
  {
    w: number
    h: number
    documentId?: string
    name?: string
    doc_type?: string
    locked?: boolean
    collapsed?: boolean
  }
>

export const DOC_PREVIEW_WIDTH = 1260
export const DOC_PREVIEW_HEIGHT = 865
export const DOC_PREVIEW_MIN_WIDTH = 600  // Minimum width to fit buttons
export const DOC_PREVIEW_MIN_HEIGHT = 400 // Minimum height for content + buttons

// Thumbnail card dimensions — sized to fill a 2×2 grid matching Figma design
export const ICON_WIDTH = 480
export const ICON_HEIGHT = 380

export const DocumentPreviewShapeComponent = ({ shape }: { shape: DocPreviewShape }) => {
  const { t } = useTranslation()
  const editor = useEditor()

  const isSelected = useValue(
    'is selected for icon',
    () => editor.getSelectedShapeIds().includes(shape.id),
    [editor, shape.id]
  )

  const zoom = useValue('zoom for icon', () => editor.getZoomLevel(), [editor])

  const { props: { documentId, name, doc_type } } = shape
  // Use resolved type from meta for display (icon/label/thumbnail), but keep
  // original doc_type in props for backend persistence (backend needs 'graph').
  const displayDocType = (shape.meta?.resolved_doc_type as string) || doc_type

  const handleOpen = useCallback(() => {
    editor.updateShape({
      id: shape.id,
      type: 'DocPreview',
      meta: { ...shape.meta, previewRequested: Date.now() },
    })
  }, [editor, shape.id, shape.meta])

  // Kebab "Remove from Board" → delete the tldraw shape. BoardCanvas
  // has a storeChange listener that catches the deletion and syncs
  // selectedDocuments / persists the board, so we don't need to touch
  // those stores here.
  const handleRemoveFromBoard = useCallback(() => {
    editor.deleteShape(shape.id)
  }, [editor, shape.id])

  // A live time-series card keeps the tags picked in its picker in the shape's meta, so they
  // survive a reload of the board.
  const handleLiveTagsChange = useCallback(
    (next: string[]) => {
      editor.updateShape({ id: shape.id, type: 'DocPreview', meta: { liveTags: next } })
    },
    [editor, shape.id]
  )
  const liveTags = Array.isArray(shape.meta?.liveTags) ? (shape.meta.liveTags as string[]) : undefined

  const updateDocumentInStore = useAppStore((s) => s.updateDocumentName)
  const setRenamedDocName = useAppStore((s) => s.setRenamedDocName)

  const renamingRef = useRef(false)

  const handleRename = useCallback(async (newName: string) => {
    if (renamingRef.current) return
    if (!documentId) {
      // A card with no documentId can't be renamed. Surface it loudly instead
      // of silently no-op'ing, so the failure is visible (and any future card
      // type that forgets to set documentId doesn't just appear to "do nothing").
      toast.error(t('board:toasts.failed_to_rename_document'))
      return
    }
    renamingRef.current = true

    // The label on a P&ID/PFD card is the SOURCE document's name
    // (graph.properties.pnid), not the graph row's own name — so renaming the
    // card must rename that source document, the entity the user sees. Regular
    // docs rename themselves. Fall back to the card's own id when the source
    // can't be resolved from the loaded documents.
    const docs = useAppStore.getState().documents
    const selfDoc = docs.find((d) => d.id === documentId)
    const targetId =
      (selfDoc?.type === 'graph' ? selfDoc.properties?.pnid : undefined) || documentId

    const previousName = shape.props.name
    // Optimistic, applied immediately so the card repaints before the backend
    // round-trip:
    //  1. the shape prop (instant repaint),
    //  2. the rename overlay keyed by the card's own id — without it the
    //     createNodes rebuild re-derives a graph card's label from the (stale)
    //     source name in pnidDocuments, or a dedup-missing doc's label from a
    //     pre-rename backend refetch, and clobbers this rename,
    //  3. the documents store for the renamed target (keeps other consumers in
    //     sync; the source doc for graph cards).
    editor.updateShape({
      id: shape.id,
      type: 'DocPreview',
      props: { ...shape.props, name: newName },
    })
    setRenamedDocName(documentId, newName)
    updateDocumentInStore?.(targetId, newName)
    try {
      await apiService.renameDocument(targetId, newName)
      toast.success(t('board:toasts.document_renamed'))
    } catch {
      // Revert on failure — keep shape, overlay and store consistent.
      const fallbackName = previousName ?? documentId
      editor.updateShape({
        id: shape.id,
        type: 'DocPreview',
        props: { ...shape.props, name: fallbackName },
      })
      setRenamedDocName(documentId, fallbackName)
      updateDocumentInStore?.(targetId, fallbackName)
      toast.error(t('board:toasts.failed_to_rename_document'))
    } finally {
      renamingRef.current = false
    }
  }, [editor, shape.id, shape.props, documentId, updateDocumentInStore, setRenamedDocName, t])

  return (
    <HTMLContainer
      style={{
        width: `${ICON_WIDTH}px`,
        height: `${ICON_HEIGHT}px`,
        overflow: 'visible',
        pointerEvents: 'all',
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
    >
      <DocumentIcon
        documentId={documentId}
        name={name}
        docType={displayDocType}
        width={ICON_WIDTH}
        height={ICON_HEIGHT}
        isSelected={isSelected}
        zoom={zoom}
        onOpen={handleOpen}
        onRename={handleRename}
        onRemoveFromBoard={handleRemoveFromBoard}
        liveTags={liveTags}
        onLiveTagsChange={handleLiveTagsChange}
      />
    </HTMLContainer>
  )
}

// No custom indicator needed — tldraw's default selection bounds handle icon selection
const DocumentPreviewIndicator = () => null

export class DocumentPreviewShapeUtil extends ShapeUtil<DocPreviewShape> {
  static override type = 'DocPreview' as const

  getDefaultProps(): DocPreviewShape['props'] {
    return {
      w: ICON_WIDTH,
      h: ICON_HEIGHT,
      collapsed: true,
    }
  }

  getGeometry() {
    return new Rectangle2d({
      width: ICON_WIDTH,
      height: ICON_HEIGHT,
      isFilled: true,
    })
  }

  component(shape: DocPreviewShape) {
    if (!shape) return null
    return <DocumentPreviewShapeComponent shape={shape} />
  }

  indicator() {
    return <DocumentPreviewIndicator />
  }

  override canResize() {
    return false
  }

  override hideSelectionBoundsBg() {
    return true
  }

  override hideSelectionBoundsFg() {
    return true
  }

  // Double-click signals a preview request via shape meta.
  // BoardCanvas listens for this and opens the preview panel.
  override onDoubleClick(shape: DocPreviewShape): TLShapePartial<DocPreviewShape> | void {
    return {
      id: shape.id,
      type: 'DocPreview',
      meta: {
        ...shape.meta,
        previewRequested: Date.now(),
      },
    }
  }

  // FS-482: provide an SVG representation for `editor.toImage()`. Without
  // this, off-DOM exports render this shape as blank — DocPreview is an
  // HTMLContainer with async-loaded thumbnails, so tldraw's default SVG
  // path has nothing to draw. Rasterize the live DOM at natural size; the
  // result is camera-independent because the inner .tl-html-container
  // doesn't carry the editor's page transform.
  override toSvg(shape: DocPreviewShape) {
    return shapeNodeToSvgImage(shape.id, ICON_WIDTH, ICON_HEIGHT)
  }
}
